import { z } from 'zod';

// Global search (plan 2E R15, after `searchIndex` and `runSearch`, sierra-panel.html
// lines 2179 and 2200). Each module's endpoint builds what the caller may see and runs
// `matchHits` on it, so the permissions a page has decide what can be found. `hay` never
// leaves the server: a hit shows only its title, sub and link.

/** The shortest query that's asked for. */
export const SEARCH_MIN = 2;

/** The most hits the shell keeps. */
export const SEARCH_LIMIT = 30;

/** Stands in `sub` for a date, shown in the viewer's own time zone. */
export const WHEN_TOKEN = '{when}';

export const SearchQuery = z.object({ q: z.string().trim().max(100) });

export const SearchHit = z.object({
  type: z.string(),
  title: z.string(),
  sub: z.string(),
  link: z.string(),
  /** What `WHEN_TOKEN` in `sub` stands for. */
  when: z.iso.datetime().nullable(),
  score: z.number().int(),
});
export type SearchHit = z.infer<typeof SearchHit>;

export const SearchResult = z.object({ hits: z.array(SearchHit) });
export type SearchResult = z.infer<typeof SearchResult>;

/** Something a module can be searched for. */
export interface Searchable extends Omit<SearchHit, 'score' | 'when'> {
  when?: string | null;
  /** Every field this viewer may search. A field they can't read must never go here. */
  hay: string;
}

/** Joins what's set: `Planning · Area18`. */
export const joinSub = (parts: readonly (string | null | undefined)[]) => parts.filter((part) => part !== null && part !== undefined && part !== '').join(' · ');

/** R15: every word in the title or the hay, in any order. A title holding the whole query scores 2, and 3 if it starts with it. */
export function matchHits(items: readonly Searchable[], query: string): SearchHit[] {
  const wanted = query.trim().toLowerCase();
  if (wanted.length < SEARCH_MIN) return [];
  const words = wanted.split(/\s+/);
  return items
    .flatMap(({ hay, when, ...item }) => {
      const title = item.title.toLowerCase();
      const text = `${title} ${hay}`.toLowerCase();
      if (!words.every((word) => text.includes(word))) return [];
      return [{ ...item, when: when ?? null, score: (title.includes(wanted) ? 2 : 0) + (title.startsWith(wanted) ? 1 : 0) }];
    })
    .sort((a, b) => b.score - a.score);
}

/** R15: hits sorted by score, each module's order kept, and the first 30. */
export const topHits = (hits: readonly SearchHit[]) => [...hits].sort((a, b) => b.score - a.score).slice(0, SEARCH_LIMIT);

/** A hit's sub, with its date in the viewer's own form. */
export const hitSub = (hit: Pick<SearchHit, 'sub' | 'when'>, format: (iso: string) => string) =>
  hit.sub
    .split(' · ')
    .map((part) => (part === WHEN_TOKEN ? (hit.when === null ? '' : format(hit.when)) : part))
    .filter((part) => part !== '')
    .join(' · ');
